// Playlist mode: real files from site/music/playlist.js on two streamed <audio> decks (never decodeAudioData'd),
// shuffled with a no-repeat bag and crossfaded with Web Audio gains (iOS ignores el.volume). Bypasses the lofi FX.
// The upcoming order is drawn ahead into a queue (every other track, capped at 50) so the listener can pick from it.
import { powerCurve } from './dsp.js';

const infoOf = e => ({ title: e.title || e.src.split('/').pop(), artist: e.artist || '', city: null, source: 'playlist',
  seed: null, bpm: null, key: null, duration: null, license: e.license || null, src: e.src });

/** createPlaylist(ac, mixer, entries, { fade, onTrack, onFail }) → { play(), pause(), next(id?), advance(now), queue, dispose() } */
export function createPlaylist(ac, mixer, entries, { fade = 6, onTrack = () => {}, onFail = () => {} } = {}) {
  if (typeof ac.createMediaElementSource !== 'function') throw new Error('this context cannot play media elements');
  const list = entries.filter(e => e && typeof e.src === 'string');
  const QUEUE = Math.min(50, Math.max(1, list.length - 1));
  let bag = [], last = -1, queue = [], qid = 0, cur = -1, fails = 0, playing = false, disposed = false, failedOut = false, jump = false;

  const decks = [0, 1].map(() => {
    const el = document.createElement('audio');
    el.preload = 'auto'; el.setAttribute('playsinline', ''); el.disableRemotePlayback = true;
    const src = ac.createMediaElementSource(el);
    const d = { el, src, inG: null, outG: null, entry: null, active: false, stallSince: 0, token: 0 };
    el.addEventListener('error', () => { if (d.active) fail(d); });
    el.addEventListener('waiting', () => { if (d.active && !d.stallSince) d.stallSince = performance.now(); });
    el.addEventListener('stalled', () => { if (d.active && !d.stallSince) d.stallSince = performance.now(); });
    el.addEventListener('playing', () => { d.stallSince = 0; if (d.active) fails = 0; });
    el.addEventListener('ended', () => { if (d.active && decks[cur] === d) crossfade(1); });   // backup for the timed crossfade
    return d;
  });

  /** Fisher-Yates bag; a new bag never starts with the entry that just played. */
  function draw() {
    if (!bag.length) {
      bag = list.map((_, i) => i);
      for (let i = bag.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [bag[i], bag[j]] = [bag[j], bag[i]]; }
      if (bag.length > 1 && bag[bag.length - 1] === last) [bag[0], bag[bag.length - 1]] = [bag[bag.length - 1], bag[0]];
    }
    return (last = bag.pop());
  }
  /** queue items get their own id: the same track can be queued twice across bags */
  function fill() { while (list.length && queue.length < QUEUE) queue.push({ id: ++qid, idx: draw() }); }
  /** fresh gains per deck start: inG only ever gets the fade-in curve, outG only the fade-out (no curve overlap) */
  function wire(d) {
    if (d.inG) { try { d.src.disconnect(); d.inG.disconnect(); d.outG.disconnect(); } catch { /* ok */ } }
    d.inG = ac.createGain(); d.outG = ac.createGain();
    d.src.connect(d.inG); d.inG.connect(d.outG); d.outG.connect(mixer.playlistIn);
  }
  function start(d, secs) {
    fill();
    const entry = list[queue.shift().idx], token = ++d.token;
    fill();
    d.entry = entry; d.active = true; d.stallSince = performance.now();
    wire(d);
    const now = ac.currentTime;
    d.inG.gain.value = 0;
    d.inG.gain.setValueCurveAtTime(powerCurve(true), now, Math.max(0.05, secs));
    d.el.src = entry.src;
    const p = d.el.play();
    const ok = () => {
      if (token !== d.token || disposed) return;
      onTrack(infoOf(entry));
    };
    if (p && p.then) p.then(ok, () => { if (token === d.token) fail(d); }); else ok();
  }
  function stopDeck(d, secs) {
    if (!d.active) return;
    d.active = false; d.stallSince = 0;
    const now = ac.currentTime, el = d.el, token = d.token;
    d.outG.gain.setValueCurveAtTime(powerCurve(false), now, Math.max(0.05, secs));
    setTimeout(() => { if (token === d.token) { el.pause(); el.removeAttribute('src'); el.load(); } }, secs * 1000 + 200);
  }
  function crossfade(secs = fade) {
    if (disposed || failedOut || !list.length) return;
    const old = decks[cur];
    cur = cur < 0 ? 0 : 1 - cur;
    if (old) stopDeck(old, secs);
    start(decks[cur], secs);
  }
  function fail(d) {
    if (disposed || failedOut) return;
    d.active = false;
    try { d.el.pause(); } catch { /* ok */ }
    if (++fails >= 3 || list.length === 0) { failedOut = true; onFail(); return; }
    if (decks[cur] === d) crossfade(0.5);
  }

  return {
    play() {
      playing = true;
      if (cur < 0 || jump) { jump = false; crossfade(1.5); return; }
      for (const d of decks) if (d.active) { const p = d.el.play(); if (p && p.catch) p.catch(() => fail(d)); }
    },
    pause() { playing = false; for (const d of decks) d.el.pause(); },
    /** skip to the next track, or to the queued item with this id (the ones before it are dropped); while paused it plays on resume */
    next(id) {
      if (disposed || failedOut) return;
      fill();
      const k = id == null ? 0 : queue.findIndex(q => q.id === id);
      if (k < 0) return;
      queue = queue.slice(k);
      if (playing) crossfade(1.5); else if (cur >= 0) jump = true;
    },
    /** called from the clock tick: timed crossfade at duration − fade, and the 10 s stall rule */
    advance() {
      if (!playing || cur < 0 || disposed) return;
      const d = decks[cur], el = d.el;
      if (d.active && Number.isFinite(el.duration) && el.duration > fade * 2 && el.currentTime >= el.duration - fade) crossfade(fade);
      else if (d.active && d.stallSince && !el.paused && performance.now() - d.stallSince > 10000) fail(d);
      else if (d.active && d.stallSince && el.readyState >= 3 && !el.paused) d.stallSince = 0;
    },
    get current() { return cur >= 0 ? decks[cur].entry : null; },
    get queue() { fill(); return queue.map(q => ({ id: q.id, ...infoOf(list[q.idx]) })); },
    dispose() {
      disposed = true;
      for (const d of decks) {
        d.active = false;
        try { d.el.pause(); d.el.removeAttribute('src'); d.el.load(); d.src.disconnect(); if (d.inG) { d.inG.disconnect(); d.outG.disconnect(); } } catch { /* ok */ }
      }
    },
  };
}
